/**
 * Mantém a sessão do Supabase viva renovando o access token proativamente.
 *
 * O access token JWT padrão do Supabase expira em 1h. O supabase-js já faz
 * auto-refresh, mas em PWA/mobile o refresh pode ser perdido quando o app
 * fica em background ou a rede oscila. Este helper executa um refresh proativo
 * antes do token expirar, garantindo que o usuário permaneça autenticado por
 * até 24h (limite do refresh token do Supabase).
 */

import { supabase } from "@/integrations/supabase/client";

const CHECK_INTERVAL_MS = 5 * 60 * 1000; // 5 minutos
const REFRESH_WHEN_REMAINING_MS = 30 * 60 * 1000; // 30 minutos

const decodeJwtExp = (token: string | null | undefined): number | null => {
  if (!token) return null;
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    const payload = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const pad = (4 - (payload.length % 4)) % 4;
    const json = typeof atob === "function"
      ? atob(payload + "=".repeat(pad))
      : Buffer.from(payload + "=".repeat(pad), "base64").toString("utf-8");
    const parsed = JSON.parse(json) as { exp?: number };
    return typeof parsed.exp === "number" ? parsed.exp * 1000 : null;
  } catch {
    return null;
  }
};

export const shouldRefreshSession = (accessToken: string | undefined): boolean => {
  const exp = decodeJwtExp(accessToken);
  if (!exp) return true;
  return exp - Date.now() <= REFRESH_WHEN_REMAINING_MS;
};

/**
 * Inicia o keep-alive de sessão. Retorna uma função de cleanup.
 */
export const startSessionKeepAlive = (): (() => void) => {
  if (typeof window === "undefined") return () => {};

  let timer: number | null = null;
  let running = false;

  const refresh = async () => {
    if (running) return;
    const { data } = await supabase.auth.getSession();
    const token = data.session?.access_token;
    if (!shouldRefreshSession(token)) return;

    running = true;
    try {
      const { error } = await supabase.auth.refreshSession();
      if (error) {
        // Não derruba a sessão local aqui: o supabase-js e os interceptores
        // já tratam falhas de refresh. Apenas logamos silenciosamente.
        console.warn("[session-keep-alive] refresh proativo falhou:", error.message);
      }
    } catch (err) {
      console.warn("[session-keep-alive] refresh proativo exceção:", err);
    } finally {
      running = false;
    }
  };

  const tick = () => {
    void refresh();
  };

  // Primeira checagem imediata para cobrir boot com token próximo da expiração.
  tick();
  timer = window.setInterval(tick, CHECK_INTERVAL_MS);

  // Também dispara ao retornar de background/pausa, quando o intervalo pode
  // ter sido perdido.
  const handleVisibility = () => {
    if (document.visibilityState === "visible") {
      tick();
    }
  };
  document.addEventListener("visibilitychange", handleVisibility);

  return () => {
    if (timer !== null) window.clearInterval(timer);
    document.removeEventListener("visibilitychange", handleVisibility);
  };
};

/**
 * Retorna a duração restante do access token em ms, ou null se não houver token.
 */
export const getAccessTokenRemainingMs = (
  accessToken: string | null | undefined,
): number | null => {
  const exp = decodeJwtExp(accessToken);
  if (!exp) return null;
  return Math.max(0, exp - Date.now());
};
